/**
 * NewsletterCheatGuide — in-app how-to panel for the Newsletters tab.
 * Uses the shared CheatGuideShell.
 */
import React from 'react';
import {
  Copy, Sparkles, Plus, Upload, Search, Wand2, Mail, FileSpreadsheet,
  Users, HelpCircle,
} from 'lucide-react';
import CheatGuideShell, {
  Section, Choice, Btn, Pill, Warn, Note,
} from './CheatGuideShell';

export default function NewsletterCheatGuide() {
  return (
    <CheatGuideShell
      title="Cheat Guide — how to run a newsletter"
      storageKey="sb_newsletter_cheat_open"
      testId="newsletter-cheat-guide">

      <Section title="1. Start a new month" icon={<Sparkles size={13} />}>
        <Choice label="Duplicate previous month" recommended>
          Fastest. Open last month's newsletter →
          <Btn icon={<Copy size={11} />} label="Duplicate to next month" /> →
          edit the words.
        </Choice>
        <Choice label="Auto-draft with Claude">
          Click <Btn icon={<Sparkles size={11} />} label="Auto-draft with Claude" /> →
          pick month → wait ~60s. Claude writes a starter in Concierge® voice.
        </Choice>
        <Choice label="From scratch">
          Click <Btn icon={<Plus size={11} />} label="New newsletter" />.
          Use only when you have very specific content.
        </Choice>
      </Section>

      <Section title="2. Add city sub-headings" icon={<HelpCircle size={13} />}>
        Each item has a <strong>Subgroup</strong> field. Type the city ("Mumbai",
        "London", "India"). Same value = same group; the heading appears once at
        the top of that group.
      </Section>

      <Section title="3. Add images — 3 options per item" icon={<Upload size={13} />}>
        <ul className="space-y-1.5 list-none ml-0">
          <li><Btn icon={<Upload size={11} />} label="Upload" /> — your own photo</li>
          <li><Btn icon={<Sparkles size={11} />} label="AI Generate" /> — DALL-E mood image (~20s). Best for generic moods.</li>
          <li><Btn icon={<Search size={11} />} label="Web Search" /> — Tavily finds real venue photos. <strong>Best 80% of the time.</strong></li>
        </ul>
        <Note><strong style={{ color: '#7A5C2B' }}>Magic button:</strong>{' '}
          <Btn icon={<Wand2 size={11} />} label="Auto-fill images" /> at the top of the editor —
          fills every empty item in 30-60s.</Note>
      </Section>

      <Section title="4. Send — three modes" icon={<Mail size={13} />}>
        <table className="w-full text-[12px] border-collapse">
          <thead>
            <tr>
              <th className="text-left py-1 pr-3 font-semibold" style={{ color: '#7A5C2B' }}>Mode</th>
              <th className="text-left py-1 font-semibold" style={{ color: '#7A5C2B' }}>When</th>
            </tr>
          </thead>
          <tbody>
            <Row icon={<Mail size={11} />} mode="Team Preview"
                 when="Always first — sends to Dipali, Nisha, Tanisha, Shiva, Nikita, Samar for review" />
            <Row icon={<FileSpreadsheet size={11} />} mode="Bulk Handover"
                 when="Routine months. ONE email to Tanisha + CSV of recipients. She bulk-sends via Zoho Campaigns." recommended />
            <Row icon={<Users size={11} />} mode="Personalised Drafts"
                 when="High-stakes months. Per-member Zoho Desk draft with Soul-Form-reordered sections." />
          </tbody>
        </table>
      </Section>

      <Section title="5. Status flow" icon={<HelpCircle size={13} />}>
        <div className="flex items-center gap-2 flex-wrap">
          <Pill bg="#FEF3C7" fg="#92400E">Draft</Pill>
          <span style={{ color: '#9CA3AF' }}>→ click Approve →</span>
          <Pill bg="#DCFCE7" fg="#166534">Ready to Send</Pill>
          <span style={{ color: '#9CA3AF' }}>→ click Send →</span>
          <Pill bg="#DBEAFE" fg="#1E3A8A">Sent</Pill>
        </div>
        <Warn>Editing content after approving auto-reverts to Draft (safety net so
          old content doesn't accidentally ship).</Warn>
      </Section>

      <Section title="6. Recommended monthly rhythm" icon={<Sparkles size={13} />}>
        <ol className="space-y-1 list-decimal ml-5">
          <li><strong>12th</strong> — Claude auto-drafts next month (cron, 9am IST)</li>
          <li><strong>15th-20th</strong> — Edit, refine words, add/remove items</li>
          <li><strong>22nd</strong> — Auto-fill images → <strong>Send → Team Preview</strong></li>
          <li><strong>24th-25th</strong> — Team signs off → click <strong>Approve</strong></li>
          <li><strong>26th-28th</strong> — <strong>Send → Bulk Handover</strong> → Tanisha bulk-sends</li>
          <li><strong>1st of next month</strong> — Members receive</li>
        </ol>
      </Section>
    </CheatGuideShell>
  );
}

function Row({ icon, mode, when, recommended }) {
  return (
    <tr style={{ borderTop: '1px solid #E2D8C2' }}>
      <td className="py-1.5 pr-3 align-top">
        <span className="inline-flex items-center gap-1 font-semibold" style={{ color: '#1B2A4A' }}>
          {icon} {mode}{recommended && <span style={{ color: '#7A5C2B' }}> ★</span>}
        </span>
      </td>
      <td className="py-1.5 align-top" style={{ color: '#3D3528' }}>{when}</td>
    </tr>
  );
}
